import * as React from "react"
import { Col, Container, Modal, Row, Button, Table, Spinner } from "react-bootstrap"
import { Subscription } from "rxjs"
import FileSaver from "file-saver"
import { connect } from "react-redux"
import { toast } from "react-toastify"
import FormatTimestampFileName from "../../formatters/FormatTimestampFileName"
import PoolSelectionRow from "../../api/Randoms/RandomsModel/PoolSelectionRow"
import RandomSelectionRow from "../../api/Randoms/RandomsModel/RandomSelectionRow"
import RandomSelectionListGet from "../../api/Randoms/RandomSelectionListGet"
import RandomSelectionListCsvGet from "../../api/Randoms/RandomSelectionListCsvGet"
import RandomSelectionListPrintGet from "../../api/Randoms/RandomSelectionListPrintGet"
import RandomSelectionListPrintLettersGet from "../../api/Randoms/RandomSelectionListPrintLettersGet"
import CompleteRandomSelection from "./Pools/CompleteRandomSelection"
import ActionButton from "../ActionButton"
import { isPortalAdmin } from "../../authentication/isAdminRole"
import { AppState } from "../../AppState"
import { BuildUserAccess } from "../../authentication/BuildUserAccess"
import GetDonorID, { GetDonorIDTitle } from "../../formatters/RenderDonorID"

interface RandomSelectionListAuthProps {
  isPortalAdmin?: boolean
  canMatchTests?: boolean
}
interface RandomSelectionListProps {
  show: boolean
  onHide: () => void
  poolId: number
  accountId?: number
  poolName: string
  poolSelection: PoolSelectionRow
  unmaskDonorId:number
}

interface RandomSelectionListState {
  data?: RandomSelectionRow[]
  loadingCsv: boolean
  loadingPrint: boolean
  loadingPrintLetter: boolean
  showCompleteModal: boolean
  testData?: {testID: number, testTypeID: number, poolSelectionID: number}
}

class RandomSelectionList extends React.PureComponent<RandomSelectionListProps & RandomSelectionListAuthProps, RandomSelectionListState> {
  state: RandomSelectionListState = {
    loadingCsv: false,
    loadingPrint: false,
    loadingPrintLetter: false,
    showCompleteModal: false,
  }
  listSubscription?: Subscription
  csvSubscription?: Subscription
  printSubscription?: Subscription
  printLetterSubscription?: Subscription

  componentDidMount() {
    this.refreshGrid()
  }

  componentWillUnmount() {
    this.listSubscription && this.listSubscription.unsubscribe()
    this.csvSubscription && this.csvSubscription.unsubscribe()
    this.printSubscription && this.printSubscription.unsubscribe()
    this.printLetterSubscription && this.printLetterSubscription.unsubscribe()
  }

  refreshGrid = () => {
    this.listSubscription && this.listSubscription.unsubscribe()
    this.listSubscription = RandomSelectionListGet(this.props.poolId, this.props.poolSelection.PoolSelectionID, this.props.accountId || 0).subscribe(
      (data: RandomSelectionRow[]) => {
        this.setState({ data: data.filter(row => row.ID !== "" || row.Name !== "" || row.TestFor !== "") })
      }
    )
  }

  handleExportClick = () => {
    this.setState({ loadingCsv: true })
    this.csvSubscription = RandomSelectionListCsvGet(this.props.poolId, this.props.poolSelection.PoolSelectionID, this.props.poolName, this.props.accountId || 0).subscribe(
      (result: string) => result ? this.export(result) : toast.error("Data not found."),
      error => toast.error(error),
      () => this.setState({ loadingCsv: false })
    )
  }

  export = (csv: string) => {
    const csvData = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    FileSaver.saveAs(csvData, FormatTimestampFileName("RandomSelectionList", "csv", new Date()), true)
    toast.success("CSV file downloaded successfully.")
  }

  handlePrintClick = () => {
    this.setState({ loadingPrint: true })
    this.printSubscription = RandomSelectionListPrintGet(this.props.poolId, this.props.poolSelection.PoolSelectionID, this.props.accountId || 0).subscribe(
      (data: string) => this.print(data),
      error => toast.error(error),
      () => this.setState({ loadingPrint: false })
    )
  }

  print = (base64pdf: string) => {
    const pdfWindow = window.open("")
    if (pdfWindow) {
      pdfWindow.document.write("<iframe width='100%' height='100%' src='data:application/pdf;base64, " + encodeURI(base64pdf) + "'></iframe>")
    }
  }

  handlePrintLettersClick = () => {
    this.setState({ loadingPrintLetter: true })
    this.printLetterSubscription = RandomSelectionListPrintLettersGet(this.props.poolId, this.props.poolSelection.PoolSelectionID, this.props.accountId || 0).subscribe(
      (data: string) => this.print(data),
      error => toast.error(error),
      () => this.setState({ loadingPrintLetter: false })
    )
  }

  drugButton = (text: string, row: RandomSelectionRow) => {
    const testData = { testID: row.DrugTestDetailID, testTypeID: 1, poolSelectionID: this.props.poolSelection.PoolSelectionID }
    return <Button variant="underline-link" className="px-0 mb-1" onClick={() => this.setState({showCompleteModal: true, testData: testData})}>{text}</Button>
  }
  alcoholButton = (text: string, row: RandomSelectionRow) => {
    const testData = { testID: row.AlcoholTestDetailID, testTypeID: 2, poolSelectionID: this.props.poolSelection.PoolSelectionID }
    return <Button variant="underline-link" className="px-0 mb-1" onClick={() => this.setState({showCompleteModal: true, testData: testData})}>{text}</Button>
  }

  generateTestForContent = (row: RandomSelectionRow) => {
    if (row.TestFor.includes("(X)") || !this.props.canMatchTests) return row.TestFor
    const results = row.TestFor.split(",")

    if (results.length > 1) {
      if (!row.DrugIsCompleted && !row.AlcoholIsCompleted) {
        return <>{this.drugButton(results[0], row)},{this.alcoholButton(results[1], row)}</>
      }
      else if (!row.DrugIsCompleted) {
        return <>{this.drugButton(results[0], row)},{results[1]}</>
      }
      else if (!row.AlcoholIsCompleted) {
        return <>{results[0]},{this.alcoholButton(results[1], row)}</>
      }
      else {
        return row.TestFor
      }
    }
    else if (results.length === 1) {
      if (row.TestFor.includes("D") && !row.DrugIsCompleted) {
        return <>{this.drugButton(row.TestFor, row)}</>
      }
      else if (row.TestFor.includes("A") && !row.AlcoholIsCompleted) {
        return <>{this.alcoholButton(row.TestFor, row)}</>
      }
      else {
        return row.TestFor
      }
    }
    else {
      return row.TestFor
    }
  }

  onCloseCompleteModal = (withUpdate: boolean) => {
    this.setState({ showCompleteModal: false, testData: undefined }, () => { if (withUpdate) this.refreshGrid() })
  }

  render() {
    const {showCompleteModal, testData} = this.state

    return (
      <Modal backdrop="static" show={this.props.show} onHide={this.props.onHide} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>RANDOM SELECTION LIST</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container className="grid-container">
            {showCompleteModal && !!testData &&
              <CompleteRandomSelection
                testDetailID={testData.testID}
                testTypeID={testData.testTypeID}
                poolSelectionID={testData.poolSelectionID}
                onClose={this.onCloseCompleteModal} />
            }
            <Row>
              <Col>
                <h5>{this.props.poolName}</h5>{this.props.poolSelection.TimePeriod}
              </Col>
              <Col>
                <Row>
                  <Col className="text-right">
                    <ActionButton variant="secondary" size="sm" onClick={this.handleExportClick} className="mr-2" disabled={!this.state.data?.length} spin={this.state.loadingCsv} disableOnSpin>EXPORT</ActionButton>
                    <ActionButton variant="secondary" size="sm" onClick={this.handlePrintClick} className="mr-2" disabled={!this.state.data?.length} spin={this.state.loadingPrint} disableOnSpin>PRINT</ActionButton>
                  </Col>
                </Row>
                <Row>
                  <Col className="text-right my-2">
                    <ActionButton variant="secondary" size="sm" onClick={this.handlePrintLettersClick} className="mr-2" disabled={!this.state.data?.length} spin={this.state.loadingPrintLetter} disableOnSpin>PRINT NOTIFICATION LETTERS</ActionButton>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row>
              <Col>
                <Table responsive className="table-sm dark">
                  <thead>
                    <tr className="text-normal">
                      <th>COMPANY</th>
                      <th>PRIMARY ID</th>
                      <th>NAME</th>
                      <th>SELECTION DATE</th>
                      <th>TEST FOR</th>
                      <th>COMPLETED DATE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!this.state.data && <tr><td className="text-center" colSpan={6}><Spinner animation="border" variant="secondary" /></td></tr>}
                    {this.state.data && this.state.data.length === 0 && <tr><td colSpan={6}>NO SELECTIONS FOR THIS ACCOUNT.</td></tr>}
                    {this.state.data && this.state.data.length > 0 && this.state.data.map((row: RandomSelectionRow, i: number) =>
                      <tr key={i}>
                        <td className="align-middle">{row.Company}</td>
                        <td className="align-middle">{GetDonorID(row.DonorID, row.ID, this.props.unmaskDonorId)}</td>
                        <td className="align-middle">{row.Name}</td>
                        <td className="align-middle">{row.DonorSelectedAt}</td>
                        <td className="align-middle">{this.generateTestForContent(row)}</td>
                        <td className="align-middle">{row.TestCompletedAt}</td>
                      </tr>)}
                  </tbody>
                </Table>
              </Col>
            </Row>
          </Container>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={this.props.onHide}>CLOSE</Button>
        </Modal.Footer>
      </Modal>
    )
  }
}

function mapStateToProps(state: AppState): RandomSelectionListAuthProps {
  const userAccess = BuildUserAccess(state)
  return {
    isPortalAdmin: isPortalAdmin(state),
    canMatchTests: userAccess.canMatchTests
  }
}
const ConnectedRandomSelectionList = connect(mapStateToProps, null)(RandomSelectionList)
export default ConnectedRandomSelectionList